import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { launchIntentCheckout, inr } from "../lib/payRail";
import { PayRailButtons } from "../components/payments/PayRailButtons";

export default function HostedPay() {
  const { intentId } = useParams();
  const [params] = useSearchParams();
  const { api } = useAuth();
  const [intent, setIntent] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const autoLaunched = useRef(false);

  useEffect(() => {
    api.get(`/payments/intents/${intentId}`).then((r) => setIntent(r.data.intent))
      .catch((e) => setError(e?.response?.data?.detail || "This payment link is not available."));
  }, [api, intentId]);

  const pay = async (rail) => {
    setBusy(rail);
    try {
      await launchIntentCheckout(api, intentId, {
        rail, name: "Vinci Inc.", description: intent.title, prefill: { name: intent.customer_name || "", email: intent.customer_email || "" },
        onSuccess: (i) => { setIntent(i); setBusy(false); toast.success("Payment received"); }, onDismiss: () => setBusy(false),
      });
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Could not start payment"); setBusy(false); }
  };

  const autoRail = params.get("rail");
  useEffect(() => {
    if (intent && intent.status !== "paid" && (autoRail === "phonepe" || autoRail === "razorpay") && !autoLaunched.current) {
      autoLaunched.current = true;
      pay(autoRail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent, autoRail]);

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0] grid place-items-center px-6 py-12" data-testid="hosted-pay-page">
      <div className="w-full max-w-lg border border-[#C9A84C]/30 bg-[#0D0D0D] rounded-sm p-8">
        <div className="flex items-center justify-between mb-6">
          <img src="/logo-mark.png" alt="Vinci" className="h-6" />
          <span className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A84C]">Secure Payment</span>
        </div>
        {error && <p data-testid="hosted-pay-error" className="text-[#E08A8A] text-sm">{error}</p>}
        {!error && !intent && <Loader2 className="animate-spin text-[#C9A84C]" />}
        {intent && (
          <>
            <h1 className="font-serif text-2xl" data-testid="hosted-pay-title">{intent.title}</h1>
            {intent.customer_name && <p className="text-sm text-[#888] mt-1">For {intent.customer_name}{intent.customer_email && intent.customer_email !== intent.customer_name ? ` · ${intent.customer_email}` : ""}</p>}
            <div className="mt-6 border border-[#262626] rounded-sm divide-y divide-[#262626] text-sm">
              <div className="flex justify-between px-4 py-3"><span className="text-[#888]">Base amount</span><span data-testid="hosted-pay-base">{inr(intent.base_inr)}</span></div>
              {intent.gst_inr > 0 && <div className="flex justify-between px-4 py-3"><span className="text-[#888]">GST @ 18%</span><span data-testid="hosted-pay-gst">{inr(intent.gst_inr)}</span></div>}
              <div className="flex justify-between px-4 py-3 font-semibold"><span>Total payable</span><span className="text-[#C9A84C]" data-testid="hosted-pay-total">{inr(intent.total_inr)}</span></div>
            </div>
            {intent.status === "void" ? (
              <div data-testid="hosted-pay-void" className="mt-6 text-[#E08A8A] text-sm">This payment link has been cancelled. Please contact the Vinci desk if you still need to settle this amount.</div>
            ) : intent.status === "paid" ? (
              <div data-testid="hosted-pay-paid" className="mt-6 flex items-center gap-2 text-[#7FD1A5]"><CheckCircle2 size={18} /> Paid · {intent.gst_invoice_no ? `GST Invoice ${intent.gst_invoice_no}` : `Ref ${intent.ref}`}</div>
            ) : (
              <div className="mt-6" data-testid="hosted-pay-button">
                <div className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#C9A84C] mb-3">Choose how to pay</div>
                <PayRailButtons testId="hosted-pay" onPay={pay} busy={busy} amountLabel={inr(intent.total_inr)}
                                note={`UPI · Cards · Net-banking on both rails. Ref ${intent.ref}.`} />
              </div>
            )}
            <p className="mt-4 flex items-center gap-2 text-[11px] text-[#666]"><ShieldCheck size={12} className="text-[#C9A84C]" /> Payments are verified server-side before anything unlocks.</p>
          </>
        )}
      </div>
    </div>
  );
}
